import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { ProfileDropdown } from '../components/ProfileDropdown';

export function ChangePassword() {
  return (
    <ProtectedRoute>
      <ChangePasswordContent />
    </ProtectedRoute>
  );
}

function ChangePasswordContent() {
  const { user, signIn, updatePassword, loading } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!user?.email) {
      setError('You must be signed in to change your password');
      return;
    }

    if (password !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (password === currentPassword) {
      setError('New password must be different from your current password');
      return;
    }

    setIsSubmitting(true);
    try {
      const { error: verifyError } = await signIn(user.email, currentPassword);
      if (verifyError) {
        setError('Current password is incorrect');
        setIsSubmitting(false);
        return;
      }

      const { error: updateError } = await updatePassword(password);
      if (updateError) {
        throw updateError;
      }

      setMessage('Password updated successfully');
      setCurrentPassword('');
      setPassword('');
      setConfirmPassword('');
      setTimeout(() => {
        navigate(-1);
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update password');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pen-screen font-brutal min-h-screen">
      <header className="pen-topbar">
        <Link to="/dashboard" className="block">
          <img
            src="/HyroxLogo.png"
            alt="Hyrox Logo"
            className="h-9 w-auto object-contain"
          />
        </Link>
        <div className="ml-auto">
          <ProfileDropdown variant="pen" />
        </div>
      </header>

      <div className="px-4 py-8 flex items-center justify-center">
        <div className="pen-card w-full max-w-md border-t-[3px] border-t-sw-yellow !rounded-none">
          <h1 className="pen-title">Change Password</h1>
          <p className="pen-lede">
            Update the password for{' '}
            <strong className="text-white">{user?.email}</strong>
          </p>

          {error && <div className="pen-error !rounded-none mb-4">{error}</div>}
          {message && <div className="pen-success !rounded-none mb-4">{message}</div>}

          <form className="space-y-3" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="current-password" className="pen-label">
                Current password
              </label>
              <input
                id="current-password"
                name="current-password"
                type="password"
                autoComplete="current-password"
                required
                className="pen-input !rounded-none"
                placeholder="Current password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                disabled={isSubmitting || loading}
              />
            </div>
            <div>
              <label htmlFor="new-password" className="pen-label">
                New password
              </label>
              <input
                id="new-password"
                name="new-password"
                type="password"
                autoComplete="new-password"
                required
                className="pen-input !rounded-none"
                placeholder="New password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={6}
                disabled={isSubmitting || loading}
              />
            </div>
            <div>
              <label htmlFor="confirm-password" className="pen-label">
                Confirm new password
              </label>
              <input
                id="confirm-password"
                name="confirm-password"
                type="password"
                autoComplete="new-password"
                required
                className="pen-input !rounded-none"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                minLength={6}
                disabled={isSubmitting || loading}
              />
            </div>

            <button
              type="submit"
              disabled={
                isSubmitting ||
                loading ||
                !currentPassword ||
                password.length < 6 ||
                password !== confirmPassword
              }
              className="pen-btn-primary w-full !rounded-none"
            >
              {isSubmitting || loading ? 'Updating…' : 'Update Password'}
            </button>
          </form>

          <button
            type="button"
            onClick={() => navigate(-1)}
            className="pen-link block w-full mt-4"
            disabled={isSubmitting}
          >
            Go back
          </button>
        </div>
      </div>
    </div>
  );
}
